import { Outlet, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { TopNavbar } from './top-navbar';
import { MobileDrawer } from './mobile-drawer';
import { ModernFooter } from './modern-footer';
import { useStoreInfo } from '@/lib/store';
import { Toaster } from 'react-hot-toast';
import { useEffect } from 'react';
import { MaintenancePage } from '../site-lock/maintenance';
import { InstallPrompt } from '../pwa/install-prompt';
import { useDynamicFavicon } from '@/hooks/useDynamicFavicon';
import { LockNotice } from '../site-lock/lock-notice';
import { SiteNotFoundPage } from '@/pages/site-not-found';

export function RootLayout() {
  const location = useLocation();
  const { storeInfo, error, loading, lockNoticeOpen, openLockNotice, closeLockNotice } = useStoreInfo();
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  
  // Initialize dynamic favicon
  useDynamicFavicon();

  // Prevent browser/SW caching when showing error page
  useEffect(() => {
    if (!storeInfo && !loading && error) {
      // Set headers to prevent caching of not-found response
      document.documentElement.classList.add('no-cache');
      if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
        // Notify SW to skip caching this response
        window.__SKIP_CACHE__ = true;
      }
    }
  }, [storeInfo, loading, error]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (storeInfo?.site_lock === 1) {
      openLockNotice();
    }
  }, [storeInfo, openLockNotice]);

  // Only show not-found if we've completed loading and there's a genuine error (not transient)
  // A genuine error is when the API explicitly returns store-not-found, not connection errors
  if (!storeInfo && !loading && error) {
    const isGenuineNotFound = error.includes('Store not found') || error.includes('STORE_NOT_FOUND');
    if (isGenuineNotFound) {
      return <SiteNotFoundPage />;
    }
    // For other errors (connection, timeout), show a retry message instead
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center p-6">
          <div className="text-red-600 font-semibold mb-2">Unable to load storefront</div>
          <div className="text-gray-600 text-sm mb-4">{error}</div>
          <button 
            onClick={() => window.location.reload()}
            className="bg-brand-primary text-white px-6 py-2 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // While resolving store info, show a simple loading placeholder to avoid false negatives on slow networks
  if (!storeInfo && loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center p-6">
          <div className="animate-pulse h-3 w-24 bg-gray-200 rounded mb-4" />
          <div className="text-gray-600">Loading store information…</div>
        </div>
      </div>
    );
  }

  if (storeInfo?.site_lock === 2) {
    return <MaintenancePage />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <TopNavbar onMobileMenuOpen={() => setIsMobileDrawerOpen(true)} />
      <MobileDrawer isOpen={isMobileDrawerOpen} onClose={() => setIsMobileDrawerOpen(false)} />
      <main className="flex-1">
        <Outlet />
      </main>
      <ModernFooter />
      <InstallPrompt />
      <LockNotice open={lockNoticeOpen} onClose={closeLockNotice} />
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#fff',
            color: '#374151',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 10px 40px -10px rgba(236, 72, 153, 0.2)',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
}